/**
 * CreditPackageSelector
 *
 * Grid of purchasable credit packages.
 * Packages come from props (fetched by the parent from /api/billing/packages/).
 */
import React from 'react';
import {
  Grid2 as Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  Stack,
  Skeleton,
} from '@mui/material';
import { Bolt as BoltIcon, LocalOffer as SaleIcon } from '@mui/icons-material';

const fmt = (cents, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);

const CreditPackageSelector = ({
  packages,
  selected,
  onSelect,
  loading = false,
  currency = 'USD',
}) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2].map(i => (
          <Grid size={{ xs: 12, sm: 4 }} key={i}>
            <Skeleton variant="rounded" height={130} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      {(packages || []).map(pkg => {
        const isSelected = selected === pkg.id;
        const perCredit = pkg.price_cents / pkg.credits;
        const hasDiscount =
          pkg.original_price_cents > 0 && pkg.original_price_cents > pkg.price_cents;
        const discountPct = hasDiscount
          ? Math.round((1 - pkg.price_cents / pkg.original_price_cents) * 100)
          : 0;
        const pkgCurrency = (pkg.currency || currency).toUpperCase();

        return (
          <Grid size={{ xs: 12, sm: 4 }} key={pkg.id}>
            <Card
              variant={isSelected ? 'elevation' : 'outlined'}
              elevation={isSelected ? 8 : 0}
              sx={{
                height: '100%',
                border: theme =>
                  isSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : pkg.highlight
                    ? `1px solid ${theme.palette.primary.light}`
                    : undefined,
                position: 'relative',
                transition: 'all 0.15s ease',
              }}
            >
              {/* Best value badge */}
              {/* {pkg.highlight && !hasDiscount && (
                <Chip
                  label="Best value"
                  size="small"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 700,
                    fontSize: 11,
                    zIndex: 1,
                  }}
                />
              )} */}

              {/* Discount badge — replaces "Best value" when a discount is active */}
              {hasDiscount && (
                <Chip
                  icon={<SaleIcon sx={{ fontSize: '14px !important' }} />}
                  label={`-${discountPct}%`}
                  size="small"
                  color="error"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 700,
                    fontSize: 11,
                    zIndex: 1,
                  }}
                />
              )}

              <CardActionArea onClick={() => onSelect(pkg.id)} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {pkg.label}
                  </Typography>

                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <BoltIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="h5" fontWeight={700}>
                      {pkg.credits}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                      credits
                    </Typography>
                  </Stack>

                  {/* Price — show strikethrough original when discounted */}
                  {hasDiscount ? (
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        {fmt(pkg.original_price_cents, pkgCurrency)}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="error.main">
                        {fmt(pkg.price_cents, pkgCurrency)}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="h6" fontWeight={600} color="primary.main">
                      {fmt(pkg.price_cents, pkgCurrency)}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {fmt(perCredit, pkgCurrency)} / credit
                  </Typography>

                  {pkg.description && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                      {pkg.description}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default CreditPackageSelector;
